import Phaser from 'phaser';
import { frameKey, heroFrameSet, preloadFrameSet } from './assets';
import { RosterHeroId } from './heroes';
import { WorldNumber, worldsForUniverse } from './levels';
import {
  grantMorphinGrid,
  grantStone,
  grantVibranium,
  unlockHero,
  unlockPowerRangers,
} from './save';
import {
  BOSS_MORPHIN_GRID_DROPS,
  BOSS_STONE_DROPS,
  BOSS_VIBRANIUM_DROPS,
  InfinityStoneId,
  STONES,
} from './stones';
import { getCurrentMode } from './mode';

const WORLD_HERO_UNLOCK: Record<WorldNumber, RosterHeroId[]> = {
  1: ['thor'],
  2: ['hulk'],
  3: ['doctor-strange'],
  4: ['scarlet-witch'],
  5: ['captain-marvel', 'vision'],
  6: ['ant-man'],
  7: ['spin'],
  8: ['wolverine'],
  9: ['rocket'],
  10: [],
  11: [],
  12: [],
  13: [],
  14: [],
};

const WORLD_HERO_LABEL: Record<RosterHeroId, string> = {
  'captain-america': 'Cap joined!',
  'black-panther': 'Black Panther joined!',
  'iron-man': 'Iron Man joined!',
  'spider-man': 'Spider-Man joined!',
  thor: 'Thor joined!',
  hulk: 'Hulk joined!',
  'doctor-strange': 'Doctor Strange joined!',
  'scarlet-witch': 'Scarlet Witch joined!',
  'captain-marvel': 'Captain Marvel joined!',
  vision: 'Vision joined!',
  'ant-man': 'Ant-Man joined!',
  'ghost-spider': 'Ghost-Spider joined!',
  spin: 'Spin joined!',
  wolverine: 'Wolverine joined!',
  rocket: 'Rocket joined!',
  daredevil: 'Daredevil joined!',
  'human-torch': 'Human Torch joined!',
  'red-ranger': 'Red Ranger joined!',
  'green-ranger': 'Green Ranger joined!',
  'pink-ranger': 'Pink Ranger joined!',
};

const SPRITE_AVAILABLE: Partial<Record<RosterHeroId, boolean>> = {
  'captain-america': true,
  'black-panther': true,
  'iron-man': true,
  'spider-man': true,
  thor: true,
  hulk: true,
  'doctor-strange': true,
  'scarlet-witch': true,
  'captain-marvel': true,
  vision: true,
  'ant-man': true,
  'ghost-spider': true,
  spin: true,
  wolverine: true,
  rocket: true,
  daredevil: true,
  'human-torch': true,
  'red-ranger': true,
  'green-ranger': true,
  'pink-ranger': true,
};

export class WorldClearScene extends Phaser.Scene {
  private worldNumber: WorldNumber = 1;
  private totalStars = 0;
  private maxStars = 21;
  private stoneEarned: InfinityStoneId | null = null;
  private vibraniumEarned = 0;
  private morphinGridEarned = 0;
  private powerRangersJustUnlocked = false;

  constructor() {
    super('WorldClear');
  }

  init(data: { worldNumber: WorldNumber; totalStars: number; maxStars: number }) {
    this.worldNumber = data.worldNumber ?? 1;
    this.totalStars = data.totalStars ?? 0;
    this.maxStars = data.maxStars ?? 21;
  }

  preload() {
    const heroes = WORLD_HERO_UNLOCK[this.worldNumber];
    for (const heroId of heroes) {
      if (SPRITE_AVAILABLE[heroId]) {
        preloadFrameSet(this, heroFrameSet(heroId, 'idle'));
      }
    }
  }

  create() {
    this.cameras.main.setBackgroundColor('#0a0f1f');
    const heroes = WORLD_HERO_UNLOCK[this.worldNumber];
    for (const heroId of heroes) unlockHero(heroId);
    const bossLevelId = `w${this.worldNumber}-boss`;
    const mode = getCurrentMode();
    const stoneId = BOSS_STONE_DROPS[bossLevelId];
    const vibAmount = mode === 'marvel' ? BOSS_VIBRANIUM_DROPS[bossLevelId] ?? 0 : 0;
    const morphinAmount =
      mode === 'powerrangers' ? BOSS_MORPHIN_GRID_DROPS[bossLevelId] ?? 0 : 0;
    this.stoneEarned = stoneId ?? null;
    this.vibraniumEarned = vibAmount;
    this.morphinGridEarned = morphinAmount;
    if (stoneId) grantStone(stoneId);
    if (vibAmount > 0) grantVibranium(vibAmount);
    if (morphinAmount > 0) grantMorphinGrid(morphinAmount);

    const lastMarvelWorld = Math.max(...worldsForUniverse('marvel'));
    if (mode === 'marvel' && this.worldNumber === lastMarvelWorld) {
      unlockPowerRangers();
      this.powerRangersJustUnlocked = true;
    }

    this.layout();
    this.scale.on('resize', () => {
      this.children.removeAll(true);
      this.layout();
    });
  }

  private layout() {
    const w = this.scale.width;
    const h = this.scale.height;

    if (this.powerRangersJustUnlocked) {
      this.drawPowerRangersUnlockModal(w, h);
      return;
    }

    this.add
      .text(w / 2, h * 0.14, `World ${this.worldNumber} Cleared!`, {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '36px',
        color: '#ffd24a',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.drawStarTotal(w, h);
    this.drawHeroSlot(w, h);
    if (this.stoneEarned) this.drawStoneReward(w, h, this.stoneEarned);
    if (this.vibraniumEarned > 0) this.drawVibraniumReward(w, h, this.vibraniumEarned);
    if (this.morphinGridEarned > 0) this.drawMorphinGridReward(w, h, this.morphinGridEarned);
    this.drawBackButton(w, h);
  }

  private drawPowerRangersUnlockModal(w: number, h: number) {
    const overlay = this.add.graphics();
    overlay.fillStyle(0x000000, 0.88);
    overlay.fillRect(0, 0, w, h);
    overlay.setAlpha(0);
    this.tweens.add({ targets: overlay, alpha: 1, duration: 220, ease: 'Cubic.easeOut' });

    const cardW = Math.min(640, Math.floor(w * 0.82));
    const cardH = Math.min(420, Math.floor(h * 0.7));
    const cx = Math.floor(w / 2);
    const cy = Math.floor(h / 2);

    const card = this.add.container(cx, cy);
    const cardBg = this.add.graphics();
    cardBg.fillStyle(0x120428, 1);
    cardBg.fillRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, 20);
    cardBg.lineStyle(6, 0xfde047, 1);
    cardBg.strokeRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, 20);
    card.add(cardBg);
    card.setScale(0.5).setAlpha(0);

    const title = this.add
      .text(0, -cardH * 0.24, 'POWER RANGERS\nUNLOCKED!', {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: `${Math.floor(Math.min(cardW * 0.09, 48))}px`,
        color: '#fde047',
        fontStyle: 'bold',
        align: 'center',
        stroke: '#7f1d1d',
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setAlpha(0);
    card.add(title);

    const subtitle = this.add
      .text(0, cardH * 0.02, "IT'S MORPHIN TIME!", {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: `${Math.floor(Math.min(cardW * 0.055, 32))}px`,
        color: '#22c55e',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setAlpha(0);
    card.add(subtitle);

    const hint = this.add
      .text(0, cardH * 0.18, '(Toggle on the home screen)', {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '18px',
        color: '#e5e7eb',
      })
      .setOrigin(0.5)
      .setAlpha(0);
    card.add(hint);

    const btn = this.add.container(0, cardH * 0.35);
    const btnG = this.add.graphics();
    btnG.fillStyle(0x22c55e, 1);
    btnG.lineStyle(4, 0xfde047, 1);
    btnG.fillRoundedRect(-120, -30, 240, 60, 18);
    btnG.strokeRoundedRect(-120, -30, 240, 60, 18);
    btn.add(btnG);
    const btnTxt = this.add
      .text(0, 0, "Let's Go!", {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '26px',
        color: '#0a0f1f',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    btn.add(btnTxt);
    btn.setAlpha(0);
    card.add(btn);

    this.tweens.add({
      targets: card,
      scale: 1,
      alpha: 1,
      duration: 420,
      delay: 200,
      ease: 'Back.easeOut',
    });

    this.launchParticleBurst(cx, cy);

    this.tweens.add({
      targets: title,
      alpha: 1,
      duration: 400,
      delay: 620,
      ease: 'Cubic.easeOut',
    });
    this.tweens.add({
      targets: subtitle,
      alpha: 1,
      duration: 400,
      delay: 920,
      ease: 'Cubic.easeOut',
    });
    this.tweens.add({
      targets: hint,
      alpha: 1,
      duration: 400,
      delay: 1220,
      ease: 'Cubic.easeOut',
    });
    this.tweens.add({
      targets: btn,
      alpha: 1,
      duration: 400,
      delay: 1520,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: btn,
          scale: 1.06,
          duration: 700,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      },
    });

    const btnHit = this.add.zone(cx, cy + cardH * 0.35, 260, 80).setInteractive({
      useHandCursor: true,
    });
    let tapped = false;
    btnHit.on('pointerup', () => {
      if (tapped) return;
      tapped = true;
      this.tweens.add({
        targets: [overlay, card, btnHit],
        alpha: 0,
        duration: 260,
        ease: 'Cubic.easeIn',
        onComplete: () => this.scene.start('Menu'),
      });
    });
  }

  private launchParticleBurst(cx: number, cy: number) {
    const rangerColors = [
      0xef4444,
      0x22c55e,
      0xec4899,
      0x3b82f6,
      0xfacc15,
      0x0a0f1f,
    ];
    const count = 48;
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.3;
      const speed = 120 + Math.random() * 160;
      const size = 4 + Math.random() * 4;
      const color = rangerColors[i % rangerColors.length];
      const star = this.add.graphics();
      star.fillStyle(color, 1);
      star.fillCircle(0, 0, size);
      star.lineStyle(1, 0x0a0f1f, 0.6);
      star.strokeCircle(0, 0, size);
      star.setPosition(cx, cy);
      star.setAlpha(0);

      const tx = cx + Math.cos(angle) * speed * 2.2;
      const ty = cy + Math.sin(angle) * speed * 2.2;

      this.tweens.add({
        targets: star,
        alpha: 1,
        duration: 140,
        delay: 300 + i * 6,
        ease: 'Cubic.easeOut',
      });
      this.tweens.add({
        targets: star,
        x: tx,
        y: ty,
        duration: 900 + Math.random() * 400,
        delay: 300 + i * 6,
        ease: 'Cubic.easeOut',
      });
      this.tweens.add({
        targets: star,
        alpha: 0,
        duration: 500,
        delay: 900 + i * 6,
        ease: 'Cubic.easeIn',
        onComplete: () => star.destroy(),
      });
    }
  }

  private drawVibraniumReward(w: number, h: number, amount: number) {
    const cx = Math.floor(w / 2);
    const cy = Math.floor(h * 0.73);
    const r = 30;

    const container = this.add.container(cx, cy);

    const aura = this.add.graphics();
    aura.fillStyle(0xa78bfa, 0.35);
    aura.fillCircle(0, 0, r * 2.2);
    container.add(aura);
    this.tweens.add({
      targets: aura,
      scale: 1.15,
      alpha: 0.2,
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    const hex = this.add.graphics();
    const points: { x: number; y: number }[] = [];
    for (let i = 0; i < 6; i++) {
      const a = -Math.PI / 2 + (i * Math.PI) / 3;
      points.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
    }
    hex.fillStyle(0xa78bfa, 1);
    hex.lineStyle(4, 0x0a0f1f, 1);
    hex.beginPath();
    hex.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) hex.lineTo(points[i].x, points[i].y);
    hex.closePath();
    hex.fillPath();
    hex.strokePath();
    hex.fillStyle(0xffffff, 0.4);
    hex.fillCircle(-r * 0.3, -r * 0.3, r * 0.28);
    container.add(hex);

    const label = this.add
      .text(cx, cy + r + 26, `+${amount} Vibranium!`, {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '20px',
        color: '#ffd24a',
        fontStyle: 'bold',
        stroke: '#0a0f1f',
        strokeThickness: 4,
      })
      .setOrigin(0.5);

    container.setScale(0.3).setAlpha(0);
    label.setAlpha(0);
    this.tweens.add({
      targets: container,
      scale: 1,
      alpha: 1,
      duration: 420,
      delay: 800,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: label,
      alpha: 1,
      duration: 300,
      delay: 1100,
      ease: 'Cubic.easeOut',
    });
  }

  private drawMorphinGridReward(w: number, h: number, amount: number) {
    const cx = Math.floor(w / 2);
    const cy = Math.floor(h * 0.73);
    const r = 30;

    const container = this.add.container(cx, cy);

    const aura = this.add.graphics();
    aura.fillStyle(0x22c55e, 0.4);
    aura.fillCircle(0, 0, r * 2.2);
    container.add(aura);
    this.tweens.add({
      targets: aura,
      scale: 1.15,
      alpha: 0.2,
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    const coin = this.add.graphics();
    coin.fillStyle(0xfde047, 1);
    coin.lineStyle(4, 0x0a0f1f, 1);
    coin.fillCircle(0, 0, r);
    coin.strokeCircle(0, 0, r);
    coin.fillStyle(0xef4444, 1);
    coin.fillRect(-r * 0.35, -r * 0.35, r * 0.7, r * 0.7);
    coin.fillStyle(0xfde047, 1);
    coin.fillTriangle(0, -r * 0.5, r * 0.25, 0, -r * 0.25, 0);
    container.add(coin);

    const label = this.add
      .text(cx, cy + r + 26, `+${amount} Morphin Grid!`, {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '20px',
        color: '#22c55e',
        fontStyle: 'bold',
        stroke: '#0a0f1f',
        strokeThickness: 4,
      })
      .setOrigin(0.5);

    container.setScale(0.3).setAlpha(0);
    label.setAlpha(0);
    this.tweens.add({
      targets: container,
      scale: 1,
      alpha: 1,
      duration: 420,
      delay: 800,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: label,
      alpha: 1,
      duration: 300,
      delay: 1100,
      ease: 'Cubic.easeOut',
    });
  }

  private drawStoneReward(w: number, h: number, id: InfinityStoneId) {
    const spec = STONES[id];
    const cy = Math.floor(h * 0.73);
    const cx = Math.floor(w / 2);
    const r = 30;

    const container = this.add.container(cx, cy);

    const aura = this.add.graphics();
    aura.fillStyle(spec.color, 0.35);
    aura.fillCircle(0, 0, r * 2.2);
    container.add(aura);
    this.tweens.add({
      targets: aura,
      scale: 1.15,
      alpha: 0.2,
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    const gem = this.add.graphics();
    gem.fillStyle(spec.color, 1);
    gem.lineStyle(4, 0x0a0f1f, 1);
    gem.fillCircle(0, 0, r);
    gem.strokeCircle(0, 0, r);
    gem.fillStyle(0xffffff, 0.4);
    gem.fillCircle(-r * 0.3, -r * 0.3, r * 0.28);
    container.add(gem);

    const label = this.add
      .text(cx, cy + r + 26, `${spec.name} acquired!`, {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '20px',
        color: '#ffd24a',
        fontStyle: 'bold',
        stroke: '#0a0f1f',
        strokeThickness: 4,
      })
      .setOrigin(0.5);

    container.setScale(0.3).setAlpha(0);
    label.setAlpha(0);
    this.tweens.add({
      targets: container,
      scale: 1,
      alpha: 1,
      duration: 420,
      delay: 800,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: label,
      alpha: 1,
      duration: 300,
      delay: 1100,
      ease: 'Cubic.easeOut',
    });
  }

  private drawStarTotal(w: number, h: number) {
    const y = Math.floor(h * 0.25);
    const cx = Math.floor(w / 2);

    const g = this.add.graphics();
    const starSize = 22;
    drawStar(g, cx - 40, y, starSize, starSize * 0.42, 5, 0xfbbf24);

    this.add
      .text(cx + 8, y, `${this.totalStars} / ${this.maxStars}`, {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '28px',
        color: '#f4f4f4',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);
  }

  private drawHeroSlot(w: number, h: number) {
    const heroes = WORLD_HERO_UNLOCK[this.worldNumber];
    const count = heroes.length;
    if (count === 0) return;
    const cy = Math.floor(h * 0.55);
    const r = Math.min(Math.floor(h * (count > 1 ? 0.12 : 0.15)), count > 1 ? 72 : 90);
    const gap = r * 0.55;
    const totalW = count * (r * 2) + (count - 1) * gap;
    const startX = Math.floor((w - totalW) / 2 + r);

    heroes.forEach((heroId, i) => {
      const cx = startX + i * (r * 2 + gap);
      this.drawOneHero(cx, cy, r, heroId);
    });
  }

  private drawOneHero(cx: number, cy: number, r: number, heroId: RosterHeroId) {
    const sparkle = this.add.graphics();
    sparkle.lineStyle(3, 0xffd24a, 0.7);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      sparkle.beginPath();
      sparkle.moveTo(cx + Math.cos(a) * (r + 18), cy + Math.sin(a) * (r + 18));
      sparkle.lineTo(cx + Math.cos(a) * (r + 34), cy + Math.sin(a) * (r + 34));
      sparkle.strokePath();
    }
    this.tweens.add({
      targets: sparkle,
      alpha: 0.3,
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    const slot = this.add.graphics();
    slot.fillStyle(0x121a32, 1);
    slot.lineStyle(4, 0xffd24a, 1);
    slot.fillCircle(cx, cy, r);
    slot.strokeCircle(cx, cy, r);

    const hasSprite = !!SPRITE_AVAILABLE[heroId];

    if (hasSprite) {
      const set = heroFrameSet(heroId, 'idle');
      if (set.urls.length > 0 && this.textures.exists(frameKey(set.prefix, 0))) {
        const sprite = this.add.sprite(cx, cy + r * 0.15, frameKey(set.prefix, 0));
        sprite.setOrigin(0.5, 0.5);
        const scale = (r * 1.8) / 92;
        sprite.setScale(scale);
        this.tweens.add({
          targets: sprite,
          scale: scale * 1.06,
          duration: 800,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    } else {
      this.add
        .text(cx, cy, '?', {
          fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
          fontSize: `${Math.floor(r * 0.9)}px`,
          color: '#64748b',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
    }

    this.add
      .text(cx, cy + r + 22, WORLD_HERO_LABEL[heroId], {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '16px',
        color: '#ffd24a',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
  }

  private drawBackButton(w: number, h: number) {
    const cx = Math.floor(w / 2);
    const cy = Math.floor(h * 0.85);

    const container = this.add.container(cx, cy);
    const g = this.add.graphics();
    g.fillStyle(0x22c55e, 1);
    g.lineStyle(3, 0x0a0f1f, 1);
    g.fillRoundedRect(-90, -28, 180, 56, 16);
    g.strokeRoundedRect(-90, -28, 180, 56, 16);
    container.add(g);

    const mode = getCurrentMode();
    const lastMarvelWorld = Math.max(...worldsForUniverse('marvel'));
    const isMarvelFinale = mode === 'marvel' && this.worldNumber === lastMarvelWorld;
    const btnLabel = isMarvelFinale ? 'See the heroes' : 'Back to map';
    const txt = this.add
      .text(0, 0, btnLabel, {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '20px',
        color: '#0a0f1f',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    container.add(txt);

    const hit = this.add.zone(0, 0, 200, 72).setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => {
      if (isMarvelFinale) this.scene.start('Ending');
      else this.scene.start('LevelMap');
    });
    container.add(hit);
  }
}

function drawStar(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  points: number,
  fill: number,
) {
  const pts: { x: number; y: number }[] = [];
  const step = Math.PI / points;
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outerR : innerR;
    const a = -Math.PI / 2 + i * step;
    pts.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
  }
  g.fillStyle(fill, 1);
  g.lineStyle(1, 0x0a0f1f, 1);
  g.beginPath();
  g.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
  g.closePath();
  g.fillPath();
  g.strokePath();
}
